import { useCallback, useState } from "react";

import { ApproveDoctorDialog } from "@/components/admin/approve-doctor-dialog";
import { DoctorDetailsDrawer } from "@/components/admin/doctor-details-drawer";
import { RejectDoctorDialog } from "@/components/admin/reject-doctor-dialog";
import { useDoctorDecisions } from "@/hooks/admin/use-admin-doctors";
import type { Doctor } from "@/types/doctor";

/**
 * Shared review workflow (details drawer + approve/reject dialogs) used by both
 * the admin dashboard and the pending doctors page.
 */
export function useDoctorReview() {
  const [selected, setSelected] = useState<Doctor | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const closeAll = useCallback(() => {
    setDrawerOpen(false);
    setApproveOpen(false);
    setRejectOpen(false);
  }, []);

  const { approve, reject } = useDoctorDecisions(closeAll);

  const openDoctor = useCallback((doctor: Doctor) => {
    setSelected(doctor);
    setDrawerOpen(true);
  }, []);

  const askApprove = useCallback((doctor: Doctor) => {
    setSelected(doctor);
    setApproveOpen(true);
  }, []);

  const askReject = useCallback((doctor: Doctor) => {
    setSelected(doctor);
    setRejectOpen(true);
  }, []);

  const dialogs = (
    <>
      <DoctorDetailsDrawer
        doctor={selected}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onApprove={askApprove}
        onReject={askReject}
      />
      <ApproveDoctorDialog
        doctor={selected}
        open={approveOpen}
        onOpenChange={setApproveOpen}
        pending={approve.isPending}
        onConfirm={() => selected && approve.mutate(selected)}
      />
      <RejectDoctorDialog
        doctor={selected}
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        pending={reject.isPending}
        onConfirm={(reason) => selected && reject.mutate({ doctor: selected, reason })}
      />
    </>
  );

  return { openDoctor, askApprove, askReject, dialogs };
}
